<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <filter-accordion
            :loading="isLoading"
            :query-count="$_queryHandler_numberOfQueries"
            :query-changed="$_queryHandler_hasQueryChanged"
            @clear-filters="$_queryHandler_resetFilters"
          >
            <template #search>
              <search-input v-model="query.searchTerm" hide-details clearable />
            </template>

            <template #default>
              <v-row>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.partner"
                    :disabled="isLoading"
                    :items="mappedPartners"
                    custom-type="avatar"
                    label="Partner"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.source"
                    :items="reportOptions.sources"
                    :disabled="isLoading"
                    label="Source Type"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <select-list
                    v-model="query.params.event_type"
                    :items="reportOptions.types"
                    :disabled="isLoading"
                    label="Event Type"
                    clearable
                    hide-details
                  />
                </v-col>
                <v-col sm="4">
                  <datetime-picker
                    v-model="query.params.dateFrom"
                    label="From"
                    :disabled="isLoading"
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col sm="4">
                  <datetime-picker
                    v-model="query.params.dateTo"
                    label="To"
                    :disabled="isLoading"
                    hide-details
                    clearable
                  />
                </v-col>
                <v-col sm="4">
                  <text-input
                    v-model="query.params.click_id"
                    label="Click ID"
                    clearable
                    hide-details
                    :disabled="isLoading"
                  />
                </v-col>
              </v-row>
            </template>

            <template #content>
              <v-card-title>
                <span>Revenue Log (£{{ (historicTotalRevenue / 100).toFixed(2) }})</span>
              </v-card-title>
              <v-card-text>
                <data-table
                  :footer-props="footerProps"
                  :headers="revenueHeaders"
                  :items="revenueEvents"
                  :loading="isLoading"
                  :options="revenueOptions"
                  :server-items-length="revenueOptions.total"
                  dense
                  disable-sort
                  small-action
                  @update:items-per-page="onUpdateItemsPerPage($event, 'revenueOptions')"
                  @update:page="onUpdatePage($event, 'revenueOptions')"
                >
                  <template #item.partner="{ value }">
                    <router-link :to="{ name: 'partners.edit', params: { slug: value } }">{{
                      getPartnerNameFromSlug(value)
                    }}</router-link>
                  </template>
                  <template #item.date="{ value }">
                    {{ formatDate(value, { separator: '-' }) }}
                  </template>
                  <template #item.click_id="{ value }">
                    <valid-click-id :value="value" />
                  </template>
                  <template #item.amount="{ value }">
                    {{ (value / 100).toFixed(2) }}
                  </template>
                  <template #item.edit="{ value }">
                    {{ (value / 100).toFixed(2) }}
                  </template>
                  <template #item.source="{ value }">
                    {{ formatTableValue(reportOptions.sources, value) }}
                  </template>
                  <template #item.event_type="{ value }">
                    {{ formatTableValue(reportOptions.types, value) }}
                  </template>
                  <template #actions="{ item }">
                    <v-list-item @click="onClickEvent(item)">
                      <v-list-item-title>
                        <span>Show Details</span>
                      </v-list-item-title>
                    </v-list-item>
                  </template>
                </data-table>
              </v-card-text>
            </template>
          </filter-accordion>
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-card class="mt-8" outlined>
        <v-card-title>
          Expense Log
        </v-card-title>
        <v-card-text>
          <data-table
            :footer-props="footerProps"
            :headers="expenseHeaders"
            :items="expenseEvents"
            :loading="isLoading"
            :options="expenseOptions"
            :server-items-length="expenseOptions.total"
            dense
            disable-sort
            small-action
            @update:items-per-page="onUpdateItemsPerPage($event, 'expenseOptions')"
            @update:page="onUpdatePage($event, 'expenseOptions')"
          >
            <template #item.date="{ value }">
              {{ formatDate(value, { separator: '-' }) }}
            </template>
            <template #item.amount="{ value }">
              {{ (value / 100).toFixed(2) }}
            </template>
            <template #item.edit="{ value }">
              {{ (value / 100).toFixed(2) }}
            </template>
            <template #actions="{ item }">
              <v-list-item @click="onClickEvent(item)">
                <v-list-item-title>
                  <span>Show Details</span>
                </v-list-item-title>
              </v-list-item>
            </template>
          </data-table>
        </v-card-text>
      </v-card>
    </v-container>
    <generic-modal
      :value="!!selectedEvent"
      confirm-text="Okay"
      title="Event Details"
      hide-cancel
      @modal-confirm="selectedEvent = null"
      @modal-cancel="selectedEvent = null"
    >
      <template v-if="selectedEvent">
        <div
          v-for="(field, index) in selectedEventFields"
          :key="field.title"
          :class="{
            'grey lighten-2': index % 2 === 0,
          }"
          class="px-1 d-flex justify-space-between"
          style="max-width: 400px;"
        >
          <span>{{ field.title }}</span>
          <span
            :class="{
              'error--text font-weight-bold text-uppercase': !field.value,
            }"
          >
            {{ field.value || 'NOT SET' }}
          </span>
        </div>
      </template>
    </generic-modal>
  </page-loader>
</template>

<script>
// Vuex
import { mapGetters, mapMutations } from 'vuex'
// Components
import DataTable from '@/components/data-table'
import DatetimePicker from '@/components/datetime-picker'
import FilterAccordion from '@/components/filter-accordion'
import GenericModal from '@/components/modals/generic-modal'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'
import TextInput from '@/components/forms/text-input.vue'
import ValidClickId from '@/components/valid-click-id'
// Mixins
import QueryParamsHandler from '@/mixins/query-params-handler'
// Services
import { offers, partners, partnerLedger } from '@/services/offers'
// Helpers
import { formatDate, formatTime } from '@/utils/date'

export default {
  name: 'HistoricEvents',
  components: {
    DataTable,
    DatetimePicker,
    FilterAccordion,
    GenericModal,
    PageLoader,
    SearchInput,
    SelectList,
    TextInput,
    ValidClickId,
  },
  mixins: [QueryParamsHandler],
  data: () => ({
    filterDrawerOpen: false,
    isLoading: false,
    queriesDefined: ['dateTo', 'dateFrom', 'partner', 'source', 'event_type', 'click_id'],
    offers: [],
    partners: [],
    revenueHeaders: [
      { text: 'Source', value: 'source' },
      { text: 'Partner', value: 'partner' },
      { text: 'Date', value: 'date', width: 110 },
      { text: 'ID', value: 'click_id' },
      { text: 'Event Type', value: 'event_type' },
      { text: 'Country', value: 'country' },
      { text: 'Amount (£)', align: 'end', value: 'amount' },
      { text: '', align: 'end', value: 'actions' },
    ],
    revenueOptions: {
      page: 1,
      itemsPerPage: 10,
      total: 0,
    },
    revenueEvents: [],
    expenseHeaders: [
      { text: 'Ad Account', value: 'partner_portal' },
      { text: 'Date', value: 'date', width: 110 },
      { text: 'Offer Type', value: 'offer_type' },
      { text: 'Country', value: 'country' },
      { text: 'Amount (£)', align: 'end', value: 'amount' },
      { text: '', align: 'end', value: 'actions' },
    ],
    expenseOptions: {
      page: 1,
      itemsPerPage: 10,
      total: 0,
    },
    expenseEvents: [],
    footerProps: {
      'items-per-page-options': [10, 20, 30, 40, 50],
    },
    selectedEvent: null,
    historicTotalRevenue: 0,
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['formattedOption', 'getItemsForOption', 'hasOptions']),
    mappedOffers() {
      return this.offers.map(({ name, slug, type }) => ({
        key: `${name} (${this.formattedOption('offerTypes', type)})` || slug,
        value: slug,
      }))
    },
    mappedPartners() {
      return this.partners.map(({ name, slug, logoThumbnail }) => ({
        key: name || slug,
        value: slug,
        icon: logoThumbnail,
      }))
    },
    hasErrors() {
      return !this.hasOptions || this.hasPageErrors
    },
    reportOptions() {
      return this.getItemsForOption('reports') || {}
    },
    selectedEventFields() {
      if (!this.selectedEvent) return []
      const {
        amount,
        bigquery_upload_date,
        click_id,
        country,
        currency,
        date,
        exchange_rate,
        event_type,
        revenue_type,
        offer,
        partner,
        partner_portal,
        raw_amount,
        website,
      } = this.selectedEvent

      const currencySymbol = this.getCurrencySymbol(currency)

      return [
        { title: 'Partner', value: this.getPartnerNameFromSlug(partner) },
        { title: 'Event Date', value: `${formatDate(date, { format: 'words' })} at ${formatTime(date)}` },
        { title: 'Event Type', value: event_type },
        { title: 'Event Revenue Type', value: revenue_type },
        { title: 'Partner Portal', value: partner_portal },
        { title: 'Offer', value: this.getOfferNameFromSlug(offer) },
        { title: 'Click ID', value: click_id },
        { title: 'Country', value: this.formattedOption('countries', country) },
        { title: 'Website', value: website },
        { title: 'Amount', value: `${currencySymbol}${raw_amount}` },
        {
          title: `Amount Converted (£${exchange_rate} per ${currencySymbol}1)`,
          value: `£${(amount / 100).toFixed(2)}`,
        },
        {
          title: 'Uploaded',
          value: `${formatDate(bigquery_upload_date, { format: 'words' })} at ${formatTime(bigquery_upload_date)}`,
        },
      ]
    },
  },
  async created() {
    await Promise.all([this.getOffersAndPartners(), this.getHistoricRevenue(), this.getHistoricExpenditure()])
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    formatDate(str, opts) {
      return formatDate(str, opts)
    },
    formatTableValue(options, value) {
      const found = options.find(option => option.value === value)
      return found ? found.key : value
    },
    formatTime(str) {
      return formatTime(str)
    },
    getCurrencySymbol(currency) {
      switch (currency) {
        case 'USD':
        default:
          return '$'
        case 'GBP':
          return '£'
        case 'EUR':
          return '€'
      }
    },
    getPartnerNameFromSlug(slug) {
      const found = this.mappedPartners.find(partner => partner.value === slug)
      return found ? found.key : slug
    },
    getOfferNameFromSlug(slug) {
      const found = this.mappedOffers.find(offer => offer.value === slug)
      return found ? found.key : slug
    },
    async getHistoricExpenditure() {
      this.isLoading = true
      try {
        const { page, itemsPerPage: perPage } = this.expenseOptions
        const { data, pagination } = await partnerLedger.getFilteredEvents({
          filters: { type: 'expense', bigquery_upload_dateIsSet: true },
          page,
          perPage,
        })

        this.expenseOptions.total = pagination.total
        this.$set(this, 'expenseEvents', data)
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to fetch event logs',
        })
      }
      this.isLoading = false
    },
    async getHistoricRevenue() {
      this.isLoading = true
      try {
        const { page, itemsPerPage: perPage } = this.revenueOptions
        const { partner, source, dateFrom, dateTo, click_id, event_type } = this.query.params
        const { searchTerm: search } = this.query

        const { data, pagination, aggregation } = await partnerLedger.getFilteredEvents({
          filters: {
            type: 'revenue',
            bigquery_upload_dateIsSet: true,
            partner,
            source,
            event_type,
            click_id,
            dateGreaterThanOrEqualTo: dateFrom,
            dateLessThanOrEqualTo: dateTo,
          },
          aggregationType: 'sum',
          aggregationProperty: 'amount',
          search,
          page,
          perPage,
        })

        this.historicTotalRevenue = aggregation?.value ?? 0
        this.revenueOptions.total = pagination.total
        this.$set(this, 'revenueEvents', data)
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to fetch event logs',
        })
      }
      this.isLoading = false
    },
    async getOffersAndPartners() {
      try {
        const { data: offerData } = await offers.getAllOffers({ compact: true })
        this.offers = offerData
        const { data: partnerData } = await partners.getAllPartners({
          compact: true,
          filters: { archivedContains: [true, false] },
        })
        this.partners = partnerData
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve Offers & Partners',
        })
      }
    },
    async onUpdateItemsPerPage(value, optionsKey) {
      this[optionsKey].itemsPerPage = value === -1 ? undefined : value

      if (optionsKey === 'revenueOptions') await this.getHistoricRevenue()
      else await this.getHistoricExpenditure()
    },
    async onUpdatePage(value, optionsKey) {
      this[optionsKey].page = value

      if (optionsKey === 'revenueOptions') await this.getHistoricRevenue()
      else await this.getHistoricExpenditure()
    },
    async handleQueryUpdate() {
      await Promise.all([this.getHistoricRevenue(), this.getHistoricExpenditure()])
    },
    onClickEvent(event) {
      this.selectedEvent = event
    },
    clearFilters() {
      Object.keys(this.query.params).forEach(param => {
        this.$set(this.query.params, param, undefined)
      })
      this.query.searchTerm = ''
    },
  },
}
</script>
